import * as Sentry from "@sentry/react";

// DSN sources, in priority order:
//   1. Build-time env var (VITE_SENTRY_DSN_WEB) — optional, lets local dev override
//   2. Runtime fetch from /functions/v1/public-config — preferred for hosted builds
//      so the DSN never has to live in the repo or .env file.
const BUILD_TIME_DSN = import.meta.env.VITE_SENTRY_DSN_WEB as string | undefined;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;

let initialized = false;

function configure(dsn: string) {
  if (initialized) return;
  initialized = true;
  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // Performance tracing. browserTracingIntegration is REQUIRED for
      // tracesSampleRate to do anything — it captures page-load and navigation
      // transactions plus Web Vitals (LCP, CLS, INP) as real-user measurements.
      integrations: [Sentry.browserTracingIntegration()],
      // Fraction of transactions sampled. Tune to your Sentry quota — 0.1 (10%)
      // is a sensible default for this traffic level.
      tracesSampleRate: 0.1,
      // NOTE: intentionally no tracePropagationTargets. Adding the Supabase
      // origin here would inject sentry-trace/baggage headers into cross-origin
      // requests, which can fail Supabase's CORS preflight and break data loads.
      // Frontend-only tracing avoids that while still capturing Web Vitals.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error promise rejection captured",
        "Failed to fetch",
        "Load failed",
        "NetworkError when attempting to fetch resource",
      ],
      beforeSend(event) {
        const msg = event.message || event.exception?.values?.[0]?.value || "";
        if (typeof msg === "string" && /(IMG|image).*load|cover.*broken/i.test(msg)) {
          return null;
        }
        return event;
      },
    });
  } catch (err) {
    console.warn("[sentry] init failed:", err);
  }
}

export function initSentry() {
  if (initialized) return;

  // 1. Synchronous path — build-time env var
  if (BUILD_TIME_DSN) {
    configure(BUILD_TIME_DSN);
    return;
  }

  // 2. Async path — fetch from public-config edge function
  if (!SUPABASE_URL) return;
  fetch(`${SUPABASE_URL}/functions/v1/public-config`, { cache: "force-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((cfg) => {
      const dsn = cfg?.sentryDsnWeb;
      if (typeof dsn === "string" && dsn.startsWith("https://")) {
        configure(dsn);
      }
    })
    .catch(() => {
      // Sentry just stays disabled — never block the app
    });
}

/** Capture an exception with optional context. Safe no-op if Sentry isn't configured. */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    Sentry.captureException(error, { extra: context });
  } catch {
    /* swallow */
  }
}

/** Capture a non-error message (warnings, audit findings). */
export function captureMessage(
  message: string,
  level: "fatal" | "error" | "warning" | "info" | "debug" = "info",
  context?: Record<string, unknown>
) {
  if (!initialized) return;
  try {
    Sentry.captureMessage(message, { level, extra: context });
  } catch {
    /* swallow */
  }
}
